import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from './AuthContext';

const FieldIntelNotificationsContext = createContext({});

export const useFieldIntelNotifications = () => {
  const context = useContext(FieldIntelNotificationsContext);
  if (!context) {
    throw new Error('useFieldIntelNotifications must be used within a FieldIntelNotificationsProvider');
  }
  return context;
};

export const FieldIntelNotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);

  const totalUnread = unreadIds.size;

  // Initial + refresh fetch of unread call_log IDs visible to this user.
  // Computed server-side (get_unread_field_intel_ids) with the same
  // visibility resolver the feed's RLS uses, so the badge never counts
  // activity the feed won't show.
  const fetchUnread = useCallback(async () => {
    if (!user?.id) {
      setUnreadIds(new Set());
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_unread_field_intel_ids');
      if (error) throw error;
      setUnreadIds(new Set(data || []));
    } catch (err) {
      console.error('[FieldIntelNotifications] fetchUnread error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const markRead = useCallback(async (callLogId) => {
    if (!user?.id || !callLogId) return;
    // Optimistic remove
    setUnreadIds(prev => {
      if (!prev.has(callLogId)) return prev;
      const next = new Set(prev);
      next.delete(callLogId);
      return next;
    });
    const { error } = await supabase.rpc('mark_call_log_read', { p_call_log_id: callLogId });
    if (error) {
      console.error('[FieldIntelNotifications] markRead error:', error);
      // Reverse optimistic update on failure
      fetchUnread();
    }
  }, [user?.id, fetchUnread]);

  const markAllRead = useCallback(async () => {
    if (!user?.id) return;
    // Optimistic clear
    setUnreadIds(new Set());
    const { error } = await supabase.rpc('mark_all_call_logs_read');
    if (error) {
      console.error('[FieldIntelNotifications] markAllRead error:', error);
      fetchUnread();
    }
  }, [user?.id, fetchUnread]);

  const isUnread = useCallback((callLogId) => unreadIds.has(callLogId), [unreadIds]);

  // Realtime: on new call_log INSERT, refetch through the server-side RPC
  // rather than trusting the raw event — the event stream carries rows for
  // accounts outside this user's team, which used to inflate the badge with
  // activity the feed could never display.
  useEffect(() => {
    if (!user?.id) return;
    fetchUnread();

    const channel = supabase
      .channel('field-intel-call-logs')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'call_logs' },
        (payload) => {
          const row = payload.new;
          if (!row || row.user_id === user.id) return;
          fetchUnread();
        }
      )
      .subscribe();
    channelRef.current = channel;

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [user?.id, fetchUnread]);

  const value = {
    unreadIds,
    totalUnread,
    loading,
    isUnread,
    markRead,
    markAllRead,
    refresh: fetchUnread,
  };

  return (
    <FieldIntelNotificationsContext.Provider value={value}>
      {children}
    </FieldIntelNotificationsContext.Provider>
  );
};
